<template>
  <div class="min-h-screen bg-white snap-y snap-mandatory overflow-y-scroll overflow-x-hidden">
    <!-- Section Hero -->
    <section class="relative h-screen flex items-center overflow-hidden snap-start w-full" style="height: calc(100vh);">
      <!-- Image de fond pleine largeur -->
      <div class="absolute inset-0">
        <img 
          src="@/assets/images/hero_img.png" 
          :alt="$t('home.hero.alt')" 
          class="w-full h-full object-cover"
        />
        <!-- Voile flou full-height avec bord progressif -->
        <div
          class="absolute inset-y-0 left-0 w-1/2 backdrop-blur-lg bg-white/10 transition-all duration-500"
          style="
            -webkit-mask-image: linear-gradient(to right, black 60%, transparent 100%);
            mask-image: linear-gradient(to right, black 60%, transparent 100%);
          "
        ></div>
      </div>
      
      <!-- Zone de contenu avec backdrop blur à gauche seulement -->
      <div class="relative z-10 w-full">
        <div class="max-w-7xl mx-auto px-8 sm:px-10 lg:px-12">
          <div class="max-w-xl md:max-w-2xl relative">
            
            <div class="relative p-4 sm:p-6 md:p-8">
              <!-- Logo et nom Anamnesis -->
              <div class="flex items-center mb-6 md:mb-8">
                <img 
                  src="/logo_anamnesis.svg" 
                  alt="Logo Anamnesis" 
                  class="h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16 opacity-90 hover:opacity-100 transition-opacity duration-300"
                />
                <h1 class="ml-3 md:ml-4 text-xl sm:text-2xl md:text-3xl font-bold text-white tracking-tight">
                  Anamnesis
                </h1>
              </div>
              
              <!-- Titre principal -->
              <h2 class="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6 tracking-tight leading-tight">
                {{ $t('home.hero.title') }}
              </h2>
              
              <!-- Sous-titre -->
              <p class="text-sm sm:text-base md:text-lg lg:text-xl text-gray-100 mb-6 md:mb-8 leading-relaxed">
                {{ $t('home.hero.subtitle') }}
              </p>
              
              <!-- CTA principal -->
              <div class="flex flex-col sm:flex-row gap-3 md:gap-4">
                <router-link 
                  to="/form" 
                  class="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 md:py-3 px-5 md:px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl text-center text-sm md:text-base"
                >
                  {{ $t('home.hero.cta.create') }}
                </router-link>
                <router-link 
                  to="/dashboard" 
                  class="bg-white/90 hover:bg-white text-blue-600 font-semibold py-2.5 md:py-3 px-5 md:px-6 rounded-lg border-2 border-white/50 transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg text-center text-sm md:text-base"
                >
                  {{ $t('home.hero.cta.access') }}
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Chevron animé pour indiquer le scroll -->
      <div class="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
        <div class="flex flex-col items-center cursor-pointer" @click="scrollToNextSection">
          <span class="text-white/80 text-sm mb-3 font-medium">{{ $t('home.hero.scrollHint') }}</span>
          <ChevronDownIcon class="w-8 h-8 text-white/80 animate-bounce hover:text-white transition-colors duration-300" />
        </div>
      </div>
    </section>

    <!-- Section Partenaires & Financeurs -->
    <section class="min-h-screen flex items-center justify-center bg-white border-t border-gray-100 snap-start">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <!-- Financeurs -->
        <div class="mb-10">
          <h3 class="text-base font-semibold text-gray-600 text-center mb-6">{{ $t('home.partners.funders') }}</h3>
          <div class="flex flex-wrap justify-center items-center gap-12 mb-6">
            <div class="text-center">
              <div class="h-16 flex items-center justify-center mb-2">
                <span class="text-sm font-medium text-gray-500 bg-gray-100 px-4 py-2 rounded">{{ $t('home.partners.fsp.name') }}</span>
              </div>
              <p class="text-xs text-gray-400">{{ $t('home.partners.fsp.description') }}</p>
            </div>
            <div class="text-center">
              <div class="h-16 flex items-center justify-center mb-2">
                <span class="text-sm font-medium text-gray-500 bg-gray-100 px-4 py-2 rounded">{{ $t('home.partners.infratech.name') }}</span>
              </div>
              <p class="text-xs text-gray-400">{{ $t('home.partners.infratech.description') }}</p>
            </div>
            <div class="text-center">
              <div class="h-16 flex items-center justify-center mb-2">
                <span class="text-sm font-medium text-gray-500 bg-gray-100 px-4 py-2 rounded">{{ $t('home.partners.erihs.name') }}</span>
              </div>
              <p class="text-xs text-gray-400">{{ $t('home.partners.erihs.description') }}</p>
            </div>
          </div>
        </div>

        <!-- Porteur & Partenaires -->
        <div>
          <h3 class="text-base font-semibold text-gray-600 text-center mb-6">{{ $t('home.partners.partners') }}</h3>
          <div class="flex flex-wrap justify-center items-center gap-8">
            <!-- MAP/CNRS - Porteur -->
            <div class="text-center">
              <div class="h-20 w-40 flex items-center justify-center mb-3">
                <img 
                  src="@/assets/images/logos/map.png" 
                  :alt="$t('home.partners.map.name')" 
                  class="max-h-full max-w-full object-contain"
                />
              </div>
              <p class="text-sm font-semibold text-gray-700">{{ $t('home.partners.map.role') }}</p>
            </div>
            
            <!-- CICRP -->
            <div class="text-center">
              <div class="h-20 w-32 flex items-center justify-center mb-3">
                <img 
                  src="@/assets/images/logos/cicrp.png" 
                  :alt="$t('home.partners.cicrp.name')" 
                  class="max-h-full max-w-full object-contain"
                />
              </div>
              <p class="text-sm text-gray-600">{{ $t('home.partners.cicrp.role') }}</p>
            </div>
            
            <!-- LRMH -->
            <div class="text-center">
              <div class="h-20 w-32 flex items-center justify-center mb-3">
                <img 
                  src="@/assets/images/logos/lrmh.png" 
                  :alt="$t('home.partners.lrmh.name')" 
                  class="max-h-full max-w-full object-contain"
                />
              </div>
              <p class="text-sm text-gray-600">{{ $t('home.partners.lrmh.role') }}</p>
            </div>
            
            <!-- CNRS -->
            <div class="text-center">
              <div class="h-20 w-32 flex items-center justify-center mb-3">
                <img 
                  src="@/assets/images/logos/cnrs.png" 
                  :alt="$t('home.partners.cnrs.name')" 
                  class="max-h-full max-w-full object-contain"
                />
              </div>
              <p class="text-sm text-gray-600">{{ $t('home.partners.cnrs.role') }}</p>
            </div>
            
            <!-- Fondation Sciences du Patrimoine -->
            <div class="text-center">
              <div class="h-20 w-32 flex items-center justify-center mb-3">
                <img 
                  src="@/assets/images/logos/fondation_sp.png" 
                  :alt="$t('home.partners.fondation.name')" 
                  class="max-h-full max-w-full object-contain"
                />
              </div>
              <p class="text-sm text-gray-600">{{ $t('home.partners.fondation.role') }}</p>
            </div>
          </div>
          
          <!-- CTA Laboratoire MAP -->
          <div class="text-center mt-8">
            <a 
              href="#" 
              class="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium transition-colors duration-300"
            >
              {{ $t('home.partners.discover') }}
              <ArrowRightIcon class="w-4 h-4 ml-2" />
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- Section Fonctionnalités principales -->
    <section class="min-h-screen flex items-center justify-center bg-gray-50 snap-start">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div class="text-center mb-12">
          <h2 class="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            {{ $t('home.features.title') }}
          </h2>
          <p class="text-base text-gray-600 max-w-2xl mx-auto">
            {{ $t('home.features.subtitle') }}
          </p>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <!-- Documentation structurée -->
          <div class="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
            <div class="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
              <ListIcon class="w-6 h-6 text-blue-600" />
            </div>
            <h3 class="text-lg font-semibold text-gray-900 mb-3">{{ $t('home.features.documentation.title') }}</h3>
            <p class="text-sm text-gray-600 leading-relaxed mb-4">
              {{ $t('home.features.documentation.description') }}
            </p>
            <a href="/fonctionnalites#documentation-structuree" class="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center text-sm">
              {{ $t('home.features.documentation.link') }}
              <ArrowRightIcon class="w-4 h-4 ml-1" />
            </a>
          </div>
          
          <!-- Gestion collaborative -->
          <div class="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
            <div class="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
              <UsersIcon class="w-6 h-6 text-green-600" />
            </div>
            <h3 class="text-lg font-semibold text-gray-900 mb-3">{{ $t('home.features.collaborative.title') }}</h3>
            <p class="text-sm text-gray-600 leading-relaxed mb-4">
              {{ $t('home.features.collaborative.description') }}
            </p>
            <a href="/fonctionnalites#gestion-collaborative" class="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center text-sm">
              {{ $t('home.features.collaborative.link') }}
              <ArrowRightIcon class="w-4 h-4 ml-1" />
            </a>
          </div>
          
          <!-- Technologie adaptable -->
          <div class="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
            <div class="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
              <CircuitBoardIcon class="w-6 h-6 text-purple-600" />
            </div>
            <h3 class="text-lg font-semibold text-gray-900 mb-3">{{ $t('home.features.technology.title') }}</h3>
            <p class="text-sm text-gray-600 leading-relaxed mb-4">
              {{ $t('home.features.technology.description') }}
            </p>
            <a href="/fonctionnalites#technologie-adaptable" class="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center text-sm">
              {{ $t('home.features.technology.link') }}
              <ArrowRightIcon class="w-4 h-4 ml-1" />
            </a>
          </div>
          
          <!-- Gestion des lieux -->
          <div class="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
            <div class="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mb-4">
              <MapPinIcon class="w-6 h-6 text-yellow-600" />
            </div>
            <h3 class="text-lg font-semibold text-gray-900 mb-3">{{ $t('home.features.locations.title') }}</h3>
            <p class="text-sm text-gray-600 leading-relaxed mb-4">
              {{ $t('home.features.locations.description') }}
            </p>
            <a href="/fonctionnalites#gestion-lieux" class="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center text-sm">
              {{ $t('home.features.locations.link') }}
              <ArrowRightIcon class="w-4 h-4 ml-1" />
            </a>
          </div>
          
          <!-- Gestion des agents -->
          <div class="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
            <div class="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-4">
              <UserCircleIcon class="w-6 h-6 text-indigo-600" />
            </div>
            <h3 class="text-lg font-semibold text-gray-900 mb-3">{{ $t('home.features.agents.title') }}</h3>
            <p class="text-sm text-gray-600 leading-relaxed mb-4">
              {{ $t('home.features.agents.description') }}
            </p>
            <a href="/fonctionnalites#gestion-agents" class="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center text-sm">
              {{ $t('home.features.agents.link') }}
              <ArrowRightIcon class="w-4 h-4 ml-1" />
            </a>
          </div>
          
          <!-- Exploitation FAIR -->
          <div class="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
            <div class="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
              <DownloadIcon class="w-6 h-6 text-orange-600" />
            </div>
            <h3 class="text-lg font-semibold text-gray-900 mb-3">{{ $t('home.features.interoperability.title') }}</h3>
            <p class="text-sm text-gray-600 leading-relaxed mb-4">
              {{ $t('home.features.interoperability.description') }}
            </p>
            <a href="/fonctionnalites#exploitation-fair" class="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center text-sm">
              {{ $t('home.features.interoperability.link') }}
              <ArrowRightIcon class="w-4 h-4 ml-1" />
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- Section Actualités -->
    <section class="min-h-screen flex items-center justify-center bg-white snap-start">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div class="text-center mb-12">
          <h2 class="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            {{ $t('home.news.title') }}
          </h2>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          <!-- Article 1 -->
          <article class="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
            <div class="h-32 bg-gradient-to-br from-blue-100 to-blue-200"></div>
            <div class="p-4">
              <time class="text-xs text-gray-500">{{ $t('home.news.launch.date') }}</time>
              <h3 class="text-base font-semibold text-gray-900 mt-2 mb-2">{{ $t('home.news.launch.title') }}</h3>
              <p class="text-sm text-gray-600">{{ $t('home.news.launch.description') }}</p>
            </div>
          </article>
          
          <!-- Article 2 -->
          <article class="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
            <div class="h-32 bg-gradient-to-br from-green-100 to-green-200"></div>
            <div class="p-4">
              <time class="text-xs text-gray-500">{{ $t('home.news.partnership.date') }}</time>
              <h3 class="text-base font-semibold text-gray-900 mt-2 mb-2">{{ $t('home.news.partnership.title') }}</h3>
              <p class="text-sm text-gray-600">{{ $t('home.news.partnership.description') }}</p>
            </div>
          </article>
          
          <!-- Article 3 -->
          <article class="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
            <div class="h-32 bg-gradient-to-br from-purple-100 to-purple-200"></div>
            <div class="p-4">
              <time class="text-xs text-gray-500">{{ $t('home.news.training.date') }}</time>
              <h3 class="text-base font-semibold text-gray-900 mt-2 mb-2">{{ $t('home.news.training.title') }}</h3>
              <p class="text-sm text-gray-600">{{ $t('home.news.training.description') }}</p>
            </div>
          </article>
        </div>
        
        <!-- CTA Actualités -->
        <div class="text-center">
          <router-link 
            to="/actualites" 
            class="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-300"
          >
            {{ $t('home.news.cta') }}
            <ArrowRightIcon class="w-5 h-5 ml-2" />
          </router-link>
        </div>
      </div>
    </section>

    <!-- Section Cas d'usage -->
    <section class="min-h-screen flex items-center justify-center bg-gray-50 snap-start">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div class="text-center mb-12">
          <h2 class="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            {{ $t('home.useCases.title') }}
          </h2>
          <p class="text-base text-gray-600 max-w-2xl mx-auto">
            {{ $t('home.useCases.subtitle') }}
          </p>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          <!-- Cas d'usage 1 -->
          <div class="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1">
            <div class="h-32 bg-gradient-to-br from-amber-100 to-amber-200 rounded-lg mb-4"></div>
            <h3 class="text-lg font-semibold text-gray-900 mb-3">{{ $t('home.useCases.archeology.title') }}</h3>
            <p class="text-sm text-gray-600 mb-4">
              {{ $t('home.useCases.archeology.description') }}
            </p>
            <a href="/cas-usage/fouilles-archeologiques" class="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-300 text-sm">
              {{ $t('home.useCases.archeology.link') }}
              <ArrowRightIcon class="w-4 h-4 ml-2" />
            </a>
          </div>
          
          <!-- Cas d'usage 2 -->
          <div class="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1">
            <div class="h-32 bg-gradient-to-br from-teal-100 to-teal-200 rounded-lg mb-4"></div>
            <h3 class="text-lg font-semibold text-gray-900 mb-3">{{ $t('home.useCases.conservation.title') }}</h3>
            <p class="text-sm text-gray-600 mb-4">
              {{ $t('home.useCases.conservation.description') }}
            </p>
            <a href="/cas-usage/conservation-oeuvres" class="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-300 text-sm">
              {{ $t('home.useCases.conservation.link') }}
              <ArrowRightIcon class="w-4 h-4 ml-2" />
            </a>
          </div>
        </div>
        
        <!-- CTA Cas d'usage -->
        <div class="text-center">
          <router-link 
            to="/cas-usage" 
            class="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold text-lg transition-colors duration-300"
          >
            {{ $t('home.useCases.cta') }}
            <ArrowRightIcon class="w-5 h-5 ml-2" />
          </router-link>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <AppFooter />
  </div>
</template>

<script lang="ts">
import AppFooter from '@/components/UI/AppFooter.vue';
import {
  ArrowRightIcon,
  ChevronDownIcon,
  CircuitBoardIcon,
  DownloadIcon,
  ListIcon,
  MapPinIcon,
  UserCircleIcon,
  UsersIcon
} from "lucide-vue-next";
import { defineComponent } from "vue";

export default defineComponent({
  name: "Home",
  components: {
    ListIcon,
    UsersIcon,
    CircuitBoardIcon,
    MapPinIcon,
    UserCircleIcon,
    DownloadIcon,
    ArrowRightIcon,
    ChevronDownIcon,
    AppFooter
  },
  methods: {
    scrollToNextSection() {
      const nextSection = document.querySelector('section:nth-of-type(2)');
      if (nextSection) {
        nextSection.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  }
});
</script>

<style scoped>
/* Styles personnalisés pour les animations et effets subtils */
.transform {
  transition: transform 0.3s ease;
}

/* Scroll snap pour une navigation fluide */
.snap-y {
  scroll-snap-type: y mandatory;
}

.snap-start {
  scroll-snap-align: start;
  scroll-snap-stop: always;
}

/* Animation hover pour les cartes */
.hover\:-translate-y-1:hover {
  transform: translateY(-4px);
}

/* Styles pour l'image hero */
.object-cover {
  object-fit: cover;
}

/* Amélioration du backdrop blur */
.backdrop-blur-md {
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

/* Responsive pour les petits écrans - snap-scroll reste actif */
@media (max-width: 640px) {
  .snap-y {
    scroll-snap-type: y mandatory;
  }
}
</style>
